import { createMiddleware } from '@tanstack/react-start';

import { logger } from '~ai-tutor/lib/logger/logger';

/**
 * Generates a unique request ID for correlation
 */
function generateRequestId(): string {
  return crypto.randomUUID();
}

/**
 * Request middleware for TanStack Start API routes
 *
 * Features:
 * - Logs all incoming requests (method, URL, timestamp)
 * - Generates request ID for correlation
 * - Adds request ID to response headers
 * - Logs response status
 * - Handles errors with proper logging context
 */
export const requestLoggerMiddleware = createMiddleware().server(async ({ next, request }) => {
  const requestId = generateRequestId();
  const url = new URL(request.url);
  const timestamp = new Date().toISOString();

  logger.info(
    {
      requestId,
      method: request.method,
      url: request.url,
      pathname: url.pathname,
      search: url.search,
      timestamp,
      userAgent: request.headers.get('user-agent') || undefined,
    },
    'Incoming request',
  );

  try {
    const result = await next();
    const response = result.response;

    const logContext = {
      requestId,
      method: request.method,
      pathname: url.pathname,
      status: response.status,
    };

    if (response.status >= 500) {
      logger.error(logContext, 'Request failed');
    } else if (response.status >= 400) {
      logger.warn(logContext, 'Request client error');
    } else {
      logger.info(logContext, 'Request completed');
    }

    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;

    logger.error(
      {
        requestId,
        method: request.method,
        pathname: url.pathname,
        error: errorMessage,
        stack: errorStack,
        errorDetails: error instanceof Error ? { name: error.name, cause: error.cause } : error,
      },
      'TanStack Start Server Request Failed',
    );

    throw error;
  }
});

/**
 * Function middleware for TanStack Start server functions
 */
export const functionLoggerMiddleware = createMiddleware({
  type: 'function',
}).server(async ({ next }) => {
  const requestId = generateRequestId();
  const timestamp = new Date().toISOString();

  logger.info(
    {
      requestId,
      timestamp,
    },
    'Server function called',
  );

  try {
    const result = await next();

    logger.info(
      {
        requestId,
      },
      'Server function completed',
    );

    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;

    logger.error(
      {
        requestId,
        error: errorMessage,
        stack: errorStack,
        errorDetails: error instanceof Error ? { name: error.name, cause: error.cause } : error,
      },
      'Server function failed',
    );

    throw error;
  }
});
